import { TFunction, useTranslation } from '@mint-lib/i18n';
import { Information as InformationIcon } from '@mint-lib/icons';
// TODO: RichTooltip, RichTooltipContent and RichTooltipTrigger must be imported from '@mint-lib/tw-components/patterns',
// not from '@mint-lib/tw-components'. The patterns RichTooltipContent already renders the title (h6) and description
// (body text) with the correct typography, so pass them via the `title` and `description` props instead of custom
// children/typography. Optional `primaryButtonProps` / `secondaryButtonProps` render action buttons. It defaults to
// side="top" with a flex-col gap-4 layout, so drop redundant className/side overrides.
import {
  Chip,
  RichTooltip,
  RichTooltipContent,
  RichTooltipTrigger,
} from '@mint-lib/tw-components';
import { Suggestions } from '@mint-sdk/api-recommendations';

import type {
  SuggestionChipColors,
  SuggestionHeaderStatus,
} from '../../../types/chips.js';
import { suggestionChipColors } from '../../../utils/chips.js';

type Props = {
  status: SuggestionHeaderStatus;
  decidedBy: string;
  timeRange: string[];
};

const getSuggestionDetailsDic = (
  fullName: string,
  dateInfo: string[],
  t: TFunction,
): Record<
  SuggestionHeaderStatus,
  {
    label: string;
    tooltipText: string;
    color: SuggestionChipColors;
  }
> => {
  const [num, range] = dateInfo;

  return {
    [Suggestions.Statuses.REJECTED]: {
      label: t('Rejected'),
      color: suggestionChipColors.REJECTED,
      tooltipText: t(`Rejected {{val, relativetime}} by {{name}}`, {
        name: fullName,
        val: -num,
        range,
      }),
    },
    [Suggestions.Statuses.ACCEPTING]: {
      label: t('Accepted'),
      color: suggestionChipColors.ACCEPTING,
      tooltipText: t(
        `Accepted {{val, relativetime}} by {{name}}. Pending application`,
        {
          name: fullName,
          val: -num,
          range,
        },
      ),
    },
    [Suggestions.Statuses.ACCEPTED]: {
      label: t('Accepted'),
      color: suggestionChipColors.ACCEPTED,
      tooltipText: t(`Accepted {{val, relativetime}} by {{name}}`, {
        name: fullName,
        val: -num,
        range,
      }),
    },
    [Suggestions.Statuses.ERROR]: {
      label: t('Error'),
      color: suggestionChipColors.ERROR,
      tooltipText: t(`Expired {{val, relativetime}}`, {
        val: -num,
        range,
      }),
    },
    [Suggestions.Statuses.EXPIRED]: {
      label: t('Expired'),
      color: suggestionChipColors.EXPIRED,
      tooltipText: t(`Expired {{val, relativetime}}`, {
        val: -num,
        range,
      }),
    },
    [Suggestions.Statuses.REWORKING]: {
      label: t('Reworking'),
      color: suggestionChipColors.REWORKING,
      tooltipText: t(`Started reworking {{val, relativetime}}`, {
        val: -num,
        range,
      }),
    },
  };
};

const StatusDetails = ({ status, decidedBy, timeRange }: Props) => {
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const suggestionDict = getSuggestionDetailsDic(decidedBy, timeRange, t);

  const statusDetails = suggestionDict[status];

  return (
    <RichTooltip>
      <RichTooltipTrigger asChild>
        <Chip
          color={statusDetails.color}
          className={'whitespace-nowrap'}
          variant="outline"
        >
          <InformationIcon />
          {statusDetails.label}
        </Chip>
      </RichTooltipTrigger>
      <RichTooltipContent side="top">
        {statusDetails.tooltipText}
      </RichTooltipContent>
    </RichTooltip>
  );
};

export { StatusDetails };
